import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2, MapPin, Phone, Clock, CheckCircle2, AlertTriangle, ChevronRight,
  Activity, User, MessageCircle, ClipboardList, FileText, Camera, Navigation2,
  Building2, TestTube, AlertOctagon, ShieldCheck, Info, X, ArrowLeft,
} from "lucide-react";
import { toast, Toaster } from "sonner";

import { Button } from "@/components/ui/button";
import {
  staffPortalMe, staffPortalJobs, staffPortalSetStatus, staffPortalJobSupplies,
  dispatchChatUpload, dispatchChatSend,
} from "@/lib/api";
import DispatchChat from "@/components/field/DispatchChat";

const STATUS_FLOW = ["assigned", "en_route", "on_site", "completed"];
const STATUS_LABEL = {
  pending: "Pending", assigned: "Assigned", en_route: "En Route",
  on_site: "On Site", completed: "Completed",
};
const STATUS_COLOR = {
  pending: { c: "#9B1C1C", bg: "rgba(155,28,28,0.10)" },
  assigned: { c: "#7A5E12", bg: "rgba(212,175,55,0.18)" },
  en_route: { c: "#0B2545", bg: "rgba(11,37,69,0.10)" },
  on_site: { c: "#0F6D5D", bg: "rgba(15,109,93,0.12)" },
  completed: { c: "#475569", bg: "#E2E8F0" },
};

const LOCATION_LABEL = {
  home: "Patient Home", facility: "Facility", office: "Office / Clinic",
  assisted_living: "Assisted Living", office_hours_center: "Office-Hours Center",
  other: "Other",
};

const POLL_MS = 12_000;

export default function StaffPortal() {
  const { token } = useParams();
  const [me, setMe] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [openJobId, setOpenJobId] = useState(null);

  const refresh = useCallback(async (silent = false) => {
    if (!token) { setError("Missing access token."); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const [meData, jobData] = await Promise.all([staffPortalMe(token), staffPortalJobs(token)]);
      setMe(meData);
      setJobs(jobData.jobs || []);
      setError(null);
    } catch (err) {
      if (!silent) setError(err?.response?.data?.detail || "Could not load your jobs. Ask dispatch for a new link.");
    } finally { if (!silent) setLoading(false); }
  }, [token]);

  useEffect(() => {
    refresh(false);
    const t = setInterval(() => refresh(true), POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const setStatus = async (leadId, next) => {
    setBusyId(leadId);
    try {
      const updated = await staffPortalSetStatus(token, leadId, next);
      setJobs((arr) => {
        if (next === "completed") return arr.filter((j) => j.id !== leadId);
        return arr.map((j) => (j.id === leadId ? { ...j, ...updated } : j));
      });
      toast.success(`Marked ${STATUS_LABEL[next]}`);
      if (next === "completed") setOpenJobId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally { setBusyId(null); }
  };

  const openJob = useMemo(() => jobs.find((j) => j.id === openJobId) || null, [jobs, openJobId]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "#F8FAFC" }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
        <Toaster position="top-center" richColors closeButton />
      </div>
    );
  }
  if (error || !me) {
    return (
      <div className="min-h-screen grid place-items-center p-6" style={{ background: "#F8FAFC" }}>
        <div className="rounded-2xl border bg-white p-6 max-w-md text-center" style={{ borderColor: "#E2E8F0" }}>
          <AlertTriangle className="h-8 w-8 mx-auto mb-3" style={{ color: "#9B1C1C" }} />
          <h1 className="font-display text-lg font-semibold mb-1" style={{ color: "#0B2545" }}>Access required</h1>
          <p className="text-sm" style={{ color: "#475569" }}>{error || "Token invalid."}</p>
          <p className="text-xs mt-3" style={{ color: "#64748B" }}>
            If you're a staff member, ask Beckett or Jazmin to text you a new mobile link.
          </p>
        </div>
        <Toaster position="top-center" richColors closeButton />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }} data-testid="staff-portal">
      <header className="sticky top-0 z-10 border-b" style={{ background: "#0B2545", borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="max-w-2xl mx-auto px-4 py-3.5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "#FFD774" }}>Field Portal</div>
            <h1 className="font-display text-lg font-bold" style={{ color: "#FFFFFF" }}>
              <User className="h-4 w-4 inline mr-1 -mt-0.5" /> {me.name}
            </h1>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.6)" }}>Active jobs</div>
            <div className="font-display text-xl font-bold" style={{ color: "#FFD774" }}
                 data-testid="staff-portal-count">{jobs.length}</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        {jobs.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed py-12 text-center bg-white"
               style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
            <CheckCircle2 className="h-8 w-8 mx-auto mb-3" style={{ color: "#0F6D5D" }} />
            <p className="font-medium" style={{ color: "#0B2545" }}>You're all caught up</p>
            <p className="text-sm mt-1">New jobs will appear here when dispatch assigns them.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="staff-portal-jobs">
            {jobs.map((j) => (
              <JobCard key={j.id} job={j} busy={busyId === j.id}
                       onSetStatus={setStatus}
                       onOpenDetail={() => setOpenJobId(j.id)} />
            ))}
          </ul>
        )}
        <p className="text-[10px] text-center mt-5" style={{ color: "#94A3B8" }}>
          Updates sync to dispatch every {Math.round(POLL_MS / 1000)} s.
        </p>
      </main>

      {openJob ? (
        <JobDetailSheet job={openJob} token={token} onClose={() => setOpenJobId(null)}
                        onSetStatus={setStatus} busy={busyId === openJob.id} />
      ) : null}

      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

// ---------------------------------------------------------------------------
const JobCard = ({ job, busy, onSetStatus, onOpenDetail }) => {
  const cur = STATUS_COLOR[job.dispatch_status] || STATUS_COLOR.pending;
  const flow = STATUS_FLOW;
  const curIdx = flow.indexOf(job.dispatch_status);
  const next = curIdx >= 0 && curIdx < flow.length - 1 ? flow[curIdx + 1] : null;

  return (
    <li className="rounded-2xl border bg-white p-4 transition-shadow"
        style={{ borderColor: "#E2E8F0" }} data-testid={`staff-job-${job.id}`}>
      <button className="w-full text-left" onClick={onOpenDetail} data-testid={`staff-job-open-${job.id}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="font-display text-base font-semibold truncate" style={{ color: "#0B2545" }}>
              {job.full_name}
            </div>
            <div className="text-xs mt-0.5 truncate" style={{ color: "#64748B" }}>
              {(job.services || []).join(", ")}
            </div>
          </div>
          <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full shrink-0"
                style={{ background: cur.bg, color: cur.c }}
                data-testid={`staff-job-status-${job.id}`}>
            {STATUS_LABEL[job.dispatch_status]}
          </span>
        </div>

        <div className="space-y-1 text-xs" style={{ color: "#475569" }}>
          {job.appointment_at && (
            <div className="flex items-center gap-1.5">
              <Clock className="h-3 w-3" style={{ color: "#94A3B8" }} />
              {new Date(job.appointment_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </div>
          )}
          {job.facility_name ? (
            <div className="flex items-center gap-1.5">
              <Building2 className="h-3 w-3" style={{ color: "#94A3B8" }} />
              {job.facility_name}{job.contract_number ? ` · #${job.contract_number}` : ""}
            </div>
          ) : null}
          {job.testing_laboratory ? (
            <div className="flex items-center gap-1.5">
              <TestTube className="h-3 w-3" style={{ color: "#94A3B8" }} />
              {job.testing_laboratory}
            </div>
          ) : null}
          {job.service_address && (
            <div className="flex items-start gap-1.5">
              <MapPin className="h-3 w-3 mt-0.5 shrink-0" style={{ color: "#94A3B8" }} />
              <span className="truncate">{job.service_address}</span>
            </div>
          )}
        </div>
      </button>

      {/* Quick action row */}
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`staff-job-quick-actions-${job.id}`}>
        <QaBtn icon={Phone} label="Call" href={job.phone ? `tel:${job.phone}` : null} testId={`staff-qa-call-${job.id}`} />
        <QaBtn icon={Navigation2} label="Navigate"
               href={job.service_address ? `https://maps.google.com/?q=${encodeURIComponent(job.service_address)}` : null}
               target="_blank" testId={`staff-qa-navigate-${job.id}`} />
        <QaBtn icon={MessageCircle} label="Message" onClick={onOpenDetail} testId={`staff-qa-message-${job.id}`} />
        <QaBtn icon={ClipboardList} label="Supplies" onClick={onOpenDetail} testId={`staff-qa-supplies-${job.id}`} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {next && (
          <Button onClick={() => onSetStatus(job.id, next)} disabled={busy}
                  className="flex-1 min-w-[140px]"
                  style={{ background: "#0B2545", color: "#FFFFFF" }}
                  data-testid={`staff-advance-${job.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <ChevronRight className="h-3.5 w-3.5 mr-1.5" />}
            Mark {STATUS_LABEL[next]}
          </Button>
        )}
        {job.dispatch_status !== "completed" && job.dispatch_status !== "assigned" && (
          <Button variant="outline" disabled={busy} onClick={() => onSetStatus(job.id, "assigned")}
                  data-testid={`staff-reset-${job.id}`}>
            <Activity className="h-3.5 w-3.5 mr-1.5" /> Reset
          </Button>
        )}
      </div>
    </li>
  );
};

const QaBtn = ({ icon: Icon, label, href, onClick, target, testId }) => {
  const cls = "inline-flex items-center gap-1 h-8 px-2.5 rounded-md border text-[11px] font-semibold hover:bg-[#F8FAFC]";
  const style = { borderColor: "#E2E8F0", background: "#FFF", color: "#0B2545" };
  if (href) {
    return <a href={href} target={target} rel={target === "_blank" ? "noreferrer" : undefined}
              className={cls} style={style} data-testid={testId}>
      <Icon className="h-3.5 w-3.5" style={{ color: "#2563EB" }} /> {label}
    </a>;
  }
  return <button type="button" onClick={onClick} className={cls} style={style} data-testid={testId}>
    <Icon className="h-3.5 w-3.5" style={{ color: "#2563EB" }} /> {label}
  </button>;
};

// ---------------------------------------------------------------------------
const JobDetailSheet = ({ job, token, onClose, onSetStatus, busy }) => {
  const [tab, setTab] = useState("details");
  const [supplies, setSupplies] = useState(null);
  const [supLoad, setSupLoad] = useState(true);
  const [photoBusy, setPhotoBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    setSupLoad(true);
    staffPortalJobSupplies(token, job.id)
      .then((r) => { if (!cancel) setSupplies(r); })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancel) setSupLoad(false); });
    return () => { cancel = true; };
  }, [token, job.id]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const up = await dispatchChatUpload(job.id, file, { token });
      await dispatchChatSend(job.id, {
        text: `📷 Specimen photo — ${up.attachment_filename}`,
        quick_action: "specimen_photo",
        attachment_file_id: up.attachment_file_id,
        attachment_filename: up.attachment_filename,
        attachment_content_type: up.attachment_content_type,
      }, { token });
      toast.success("Photo sent to dispatch.");
      setTab("chat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed.");
    } finally { setPhotoBusy(false); }
  };

  const reportIssue = async () => {
    const text = window.prompt("Describe the issue:");
    if (!text) return;
    try {
      await dispatchChatSend(job.id, { text: `⚠️ ISSUE: ${text}`, quick_action: "report_issue" }, { token });
      toast.success("Issue reported to dispatch.");
      setTab("chat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed."); }
  };

  const cur = STATUS_COLOR[job.dispatch_status] || STATUS_COLOR.pending;
  const flow = STATUS_FLOW;
  const curIdx = flow.indexOf(job.dispatch_status);
  const next = curIdx >= 0 && curIdx < flow.length - 1 ? flow[curIdx + 1] : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="staff-job-detail-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white flex flex-col shadow-xl">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-start justify-between gap-2" style={{ borderColor: "#F1F5F9" }}>
          <button onClick={onClose} className="text-xs inline-flex items-center gap-1"
                  style={{ color: "#64748B" }} data-testid="staff-detail-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full"
                style={{ background: cur.bg, color: cur.c }}>
            {STATUS_LABEL[job.dispatch_status]}
          </span>
          <button onClick={onClose} className="h-7 w-7 grid place-items-center rounded-md hover:bg-[#F1F5F9]"
                  data-testid="staff-detail-close">
            <X className="h-4 w-4" style={{ color: "#475569" }} />
          </button>
        </div>

        <div className="px-4 py-3 border-b" style={{ borderColor: "#F1F5F9" }}>
          <div className="font-display text-lg font-semibold" style={{ color: "#0B2545" }}
               data-testid="staff-detail-name">{job.full_name}</div>
          <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>
            {(job.services || []).join(", ")}
            {job.request_id ? ` · #${job.request_id}` : ""}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pt-2 flex gap-1 border-b" style={{ borderColor: "#F1F5F9" }}>
          {[
            { id: "details",  label: "Details" },
            { id: "supplies", label: "Supplies" },
            { id: "chat",     label: "Chat" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    className="text-xs px-3 py-2 -mb-px border-b-2"
                    style={{
                      borderColor: tab === t.id ? "#0B2545" : "transparent",
                      color: tab === t.id ? "#0B2545" : "#64748B",
                      fontWeight: tab === t.id ? 600 : 400,
                    }}
                    data-testid={`staff-detail-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {tab === "details" && (
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="staff-detail-details">
              <DetailRow icon={Clock} label="Appointment"
                         value={job.appointment_at ? new Date(job.appointment_at).toLocaleString() : "—"} />
              <DetailRow icon={Phone} label="Phone"
                         value={job.phone} link={job.phone ? `tel:${job.phone}` : null} />
              <DetailRow icon={MapPin} label="Address"
                         value={job.service_address}
                         link={job.service_address ? `https://maps.google.com/?q=${encodeURIComponent(job.service_address)}` : null} />
              <DetailRow icon={Building2} label="Location Type"
                         value={LOCATION_LABEL[job.collection_location_type] || job.collection_location_type || "—"} />
              <DetailRow icon={TestTube} label="Testing Laboratory" value={job.testing_laboratory || "—"} />
              {job.facility_name ? (
                <DetailRow icon={Building2} label="Facility"
                           value={`${job.facility_name}${job.contract_number ? ` · Contract #${job.contract_number}` : ""}`} />
              ) : null}
              {job.special_instructions ? (
                <div className="rounded-lg border p-3 text-sm"
                     style={{ borderColor: "rgba(220,38,38,0.35)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
                     data-testid="staff-detail-special-instructions">
                  <div className="text-[10px] uppercase font-bold tracking-wider mb-1 flex items-center gap-1">
                    <AlertOctagon className="h-3 w-3" /> Special Instructions
                  </div>
                  {job.special_instructions}
                </div>
              ) : null}
              {job.access_codes ? (
                <div className="rounded-lg border p-3 text-sm"
                     style={{ borderColor: "rgba(212,175,55,0.4)", background: "rgba(212,175,55,0.10)", color: "#7A5E12" }}
                     data-testid="staff-detail-access-codes">
                  <div className="text-[10px] uppercase font-bold tracking-wider mb-1 flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> Access Codes / Building
                  </div>
                  {job.access_codes}
                </div>
              ) : null}
              {job.patient_notes ? (
                <div className="rounded-lg border p-3 text-sm bg-[#F8FAFC]" style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                     data-testid="staff-detail-patient-notes">
                  <div className="text-[10px] uppercase font-bold tracking-wider mb-1" style={{ color: "#7A5E12" }}>
                    Patient Notes
                  </div>
                  {job.patient_notes}
                </div>
              ) : null}
              {job.lab_order_url ? (
                <a href={`${job.lab_order_url}?token=${encodeURIComponent(token)}`}
                   target="_blank" rel="noreferrer"
                   className="block rounded-lg border p-3 text-sm hover:bg-[#F8FAFC]"
                   style={{ borderColor: "#E2E8F0", color: "#2563EB" }}
                   data-testid="staff-detail-lab-order-link">
                  <FileText className="h-4 w-4 inline mr-1 -mt-0.5" />
                  Open lab order — {job.lab_order_filename || "attachment"}
                </a>
              ) : null}
              {job.service_details && Object.keys(job.service_details).length > 0 ? (
                <div className="rounded-lg p-3" style={{ background: "rgba(11,37,69,0.04)" }}>
                  <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "#7A5E12" }}>Service Details</div>
                  <ul className="space-y-0.5">
                    {Object.entries(job.service_details).map(([k, v]) => (
                      <li key={k} className="text-[11px]" style={{ color: "#0B2545" }}>
                        <span style={{ color: "#64748B" }}>{k.replace(/_/g, " ")}:</span> {String(v)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="pt-2 border-t space-y-2" style={{ borderColor: "#F1F5F9" }}>
                <div className="flex flex-wrap gap-2">
                  <QaBtn icon={Camera} label={photoBusy ? "Uploading…" : "Upload photo"}
                         testId="staff-qa-upload-photo"
                         onClick={() => document.getElementById(`staff-photo-input-${job.id}`)?.click()} />
                  <input id={`staff-photo-input-${job.id}`} type="file" accept="image/*" capture="environment"
                         className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])}
                         data-testid={`staff-photo-input-${job.id}`} />
                  <QaBtn icon={AlertOctagon} label="Report issue" onClick={reportIssue} testId="staff-qa-report-issue" />
                </div>
                {next ? (
                  <Button onClick={() => onSetStatus(job.id, next)} disabled={busy}
                          className="w-full" style={{ background: "#0B2545", color: "#FFF" }}
                          data-testid={`staff-detail-advance-${job.id}`}>
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ChevronRight className="h-3.5 w-3.5 mr-1.5" />}
                    Mark {STATUS_LABEL[next]}
                  </Button>
                ) : null}
              </div>
            </div>
          )}

          {tab === "supplies" && (
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="staff-detail-supplies">
              {supLoad ? (
                <div className="grid place-items-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
                </div>
              ) : supplies ? (
                <>
                  <div className="rounded-lg border p-3 text-xs flex gap-2"
                       style={{ borderColor: "rgba(212,175,55,0.4)", background: "rgba(212,175,55,0.10)", color: "#7A5E12" }}
                       data-testid="supplies-disclaimer">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>{supplies.disclaimer}</div>
                  </div>
                  <SupplyGroup title="Kits & Tubes" items={supplies.kits} testId="supplies-kits" />
                  <SupplyGroup title="PPE" items={supplies.ppe} testId="supplies-ppe" />
                  <SupplyGroup title="Transport" items={supplies.transport} testId="supplies-transport" />
                  <SupplyGroup title="Labels" items={supplies.labels} testId="supplies-labels" />
                  {supplies.special_instructions ? (
                    <div className="rounded-lg border p-3 text-sm"
                         style={{ borderColor: "rgba(220,38,38,0.35)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}>
                      <div className="text-[10px] uppercase font-bold tracking-wider mb-1 flex items-center gap-1">
                        <AlertOctagon className="h-3 w-3" /> Special Instructions
                      </div>
                      {supplies.special_instructions}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          )}

          {tab === "chat" && (
            <div className="flex-1 overflow-hidden">
              <DispatchChat leadId={job.id} token={token} senderRoleHint="staff" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value, link }) => (
  <div className="flex items-start gap-2 text-sm">
    <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#94A3B8" }} />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-wider" style={{ color: "#94A3B8" }}>{label}</div>
      {link ? (
        <a href={link} className="text-sm break-words underline" style={{ color: "#2563EB" }}>
          {value || "—"}
        </a>
      ) : (
        <div className="text-sm break-words" style={{ color: "#0B2545" }}>{value || "—"}</div>
      )}
    </div>
  </div>
);

const SupplyGroup = ({ title, items, testId }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "#E2E8F0" }} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: "#0B2545" }}>{title}</div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" data-testid={`${testId}-check-${i}`} />
            <span style={{ color: "#0B2545" }}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
